import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Custom hook to handle unsaved changes warning
 * Works with BrowserRouter by intercepting navigation attempts
 * 
 * @param {boolean} hasChanges - Whether there are unsaved changes
 * @param {function} onBlock - Callback when navigation is blocked
 * @returns {object} - { pendingNavigation, proceedNavigation, cancelNavigation, pendingPath, safeNavigate }
 */
export const useUnsavedChanges = (hasChanges, onBlock) => {
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [pendingOptions, setPendingOptions] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const proceedRef = useRef(null);

  // Intercept Link clicks
  useEffect(() => {
    if (!hasChanges) {
      setPendingNavigation(false);
      setPendingPath(null);
      return;
    }

    const handleClick = (e) => {
      const link = e.target.closest("a[href]");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
        return;
      }

      // Check if it's an internal route
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin === window.location.origin) {
          const targetPath = url.pathname + url.search;
          const currentPath = location.pathname + location.search;

          if (targetPath !== currentPath && hasChanges) {
            e.preventDefault();
            setPendingPath(targetPath);
            setPendingNavigation(true);
            proceedRef.current = () => {
              navigate(targetPath);
              setPendingNavigation(false);
              setPendingPath(null);
            };
            onBlock?.(targetPath);
          }
        }
      } catch (err) {
        // Invalid URL, ignore
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [hasChanges, onBlock, navigate, location]);

  // Safe navigate function that checks for unsaved changes
  const safeNavigate = (to, options) => {
    const targetPath = typeof to === "string" ? to : to.pathname || "";
    const currentPath = location.pathname;

    if (targetPath !== currentPath && hasChanges) {
      setPendingPath(targetPath);
      setPendingOptions(options);
      setPendingNavigation(true);
      proceedRef.current = () => {
        navigate(to, options);
        setPendingNavigation(false);
        setPendingPath(null);
        setPendingOptions(null);
      };
      onBlock?.(targetPath);
      return;
    }

    // No changes or same path, proceed normally
    navigate(to, options);
  };

  const proceedNavigation = () => {
    if (proceedRef.current) {
      proceedRef.current();
      proceedRef.current = null;
    }
  };

  const cancelNavigation = () => {
    setPendingNavigation(false);
    setPendingPath(null);
    setPendingOptions(null);
    proceedRef.current = null;
  };

  return {
    pendingNavigation,
    proceedNavigation,
    cancelNavigation,
    pendingPath,
    safeNavigate,
  };
};
